import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, ArrowLeft, AlertCircle, MessageSquare, CheckCircle, DollarSign, Send, Clock, Link2, Download, Cloud, X, Gift, Copy, Share2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { useBusinessProfile } from '../lib/businessProfileContext';
import { useBalance } from '../lib/balanceContext';
import SubscriptionSection from '../components/SubscriptionSection';

function SettingsContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { businessProfile, refreshProfile, updateProfile } = useBusinessProfile();
  const { balance, refreshBalance } = useBalance();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [isPriceHidden, setIsPriceHidden] = useState(true);
  const [telegramLinked, setTelegramLinked] = useState(false);
  const [telegramReportEnabled, setTelegramReportEnabled] = useState(false);
  const [telegramReportTime, setTelegramReportTime] = useState('20:00');
  const [telegramLinking, setTelegramLinking] = useState(false);
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTestSending, setTelegramTestSending] = useState(false);
  const [googleDriveConnected, setGoogleDriveConnected] = useState(false);
  const [googleDriveConnecting, setGoogleDriveConnecting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportHistory, setExportHistory] = useState<any[]>([]);
  const [referralStats, setReferralStats] = useState({ total: 0, completed: 0, totalBonus: 0 });
  const [referralCopied, setReferralCopied] = useState(false);
  const [referralQR, setReferralQR] = useState<string | null>(null);

  useEffect(() => {
    if (businessProfile) {
      setSmsEnabled(businessProfile.sms_enabled ?? true);
      setIsPriceHidden(businessProfile.is_price_hidden ?? true);
      setTelegramLinked(!!businessProfile.telegram_chat_id);
      setTelegramReportEnabled(businessProfile.telegram_report_enabled ?? false);
      if (businessProfile.telegram_report_time) {
        setTelegramReportTime(businessProfile.telegram_report_time);
      }
    }
  }, [businessProfile]);

  // Check Google Drive connection status
  useEffect(() => {
    const checkGoogleDriveStatus = async () => {
      if (!user) return;

      const { data } = await supabase
        .from('google_drive_tokens')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      setGoogleDriveConnected(!!data);
    };

    checkGoogleDriveStatus();
  }, [user]);

  // Load export history
  useEffect(() => {
    const loadExportHistory = async () => {
      if (!user) return;

      const { data } = await supabase
        .from('data_exports')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (data) {
        setExportHistory(data);
      }
    };

    loadExportHistory();
  }, [user]);

  // Check URL params for Google Drive connection status
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_drive_connected') === 'true') {
      setGoogleDriveConnected(true);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('google_drive_error')) {
      const errorMsg = urlParams.get('google_drive_error');
      setError(t('googleDriveError', `Google Drive connection failed: ${errorMsg}`));
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [t]);

  // Fetch referral stats
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_user_id', user.id);
      if (data) {
        setReferralStats({
          total: data.length,
          completed: data.filter(r => r.status === 'completed').length,
          totalBonus: data.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.bonus_amount || 0), 0),
        });
      }
    })();
  }, [user]);

  // Generate referral QR code
  useEffect(() => {
    if (!businessProfile?.referral_code) return;
    import('qrcode').then(QRCode => {
      QRCode.toDataURL(`https://pmaster.uz/auth/signup?ref=${businessProfile.referral_code}`, {
        width: 200,
        margin: 1,
        color: { dark: '#1e40af', light: '#ffffff' },
      }).then(url => setReferralQR(url));
    });
  }, [businessProfile?.referral_code]);

  // Poll for link status if link code was generated
  useEffect(() => {
    if (!linkCode) return;

    const interval = setInterval(async () => {
      if (!user || !businessProfile) return;

      // Avoid refreshProfile() to prevent full-page loading spinner; fetch just what we need.
      const { data, error: fetchError } = await supabase
        .from('business_profiles')
        .select('telegram_chat_id, telegram_linked_at, telegram_report_enabled, telegram_report_time')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError || !data) return;

      updateProfile({
        telegram_chat_id: data.telegram_chat_id ?? null,
        telegram_linked_at: data.telegram_linked_at ?? null,
        telegram_report_enabled: data.telegram_report_enabled ?? false,
        telegram_report_time: data.telegram_report_time ?? null,
      } as any);

      if (data.telegram_chat_id) {
        setLinkCode(null);
        setTelegramLinking(false);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [linkCode, user, businessProfile, updateProfile]);

  const handleSmsToggle = async (enabled: boolean) => {
    if (!user || !businessProfile) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from('business_profiles')
        .update({ sms_enabled: enabled })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setSmsEnabled(enabled);
      setSuccess(true);
      await refreshProfile();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sozlamalarni yangilashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceHiddenToggle = async (hidden: boolean) => {
    if (!user || !businessProfile) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from('business_profiles')
        .update({ is_price_hidden: hidden })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setIsPriceHidden(hidden);
      setSuccess(true);
      await refreshProfile();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sozlamalarni yangilashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkTelegram = async () => {
    if (!user || !businessProfile) return;

    setTelegramLinking(true);
    setError(null);

    try {
      // Generate unique link code
      const code = crypto.randomUUID().replace(/-/g, '').substring(0, 16);

      // Store link code in database
      const { error: codeError } = await supabase
        .from('telegram_link_codes')
        .insert({
          code,
          user_id: user.id,
          business_profile_id: businessProfile.id,
        });

      if (codeError) throw codeError;

      setLinkCode(code);

      // Open Telegram bot with deep link
      const botUrl = `https://t.me/pMasterUZBot?start=link_${code}`;
      window.open(botUrl, '_blank');

      // Start polling for link status
    } catch (err) {
      setError(err instanceof Error ? err.message : t('telegramLinkFailed', 'Failed to generate link code'));
      setTelegramLinking(false);
    }
  };

  const handleTelegramReportToggle = async (enabled: boolean) => {
    if (!user || !businessProfile) return;

    setTelegramSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from('business_profiles')
        .update({ telegram_report_enabled: enabled })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setTelegramReportEnabled(enabled);
      updateProfile({ telegram_report_enabled: enabled } as any);
      setSuccess(true);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('telegramUpdateFailed', 'Failed to update settings'));
    } finally {
      setTelegramSaving(false);
    }
  };

  const handleTelegramReportTimeSave = async (time: string) => {
    if (!user || !businessProfile) return;

    setTelegramSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from('business_profiles')
        .update({ telegram_report_time: time })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      updateProfile({ telegram_report_time: time } as any);
      setSuccess(true);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('telegramUpdateFailed', 'Failed to update settings'));
    } finally {
      setTelegramSaving(false);
    }
  };

  const handleSendTestReport = async () => {
    if (!user || !businessProfile) return;

    setTelegramTestSending(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: invokeError } = await supabase.functions.invoke('send-daily-report', {
        body: { business_profile_id: businessProfile.id },
      });

      if (invokeError) throw invokeError;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('telegramTestFailed', 'Failed to send test report'));
    } finally {
      setTelegramTestSending(false);
    }
  };

  const handleConnectGoogleDrive = async () => {
    if (!user) return;

    setGoogleDriveConnecting(true);
    setError(null);

    try {
      const { data, error: connectError } = await supabase.functions.invoke('google-drive-connect');

      if (connectError) throw connectError;
      if (!data?.authUrl) throw new Error('Failed to get OAuth URL');

      // Open OAuth URL in new window
      window.open(data.authUrl, '_blank', 'width=600,height=700');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('googleDriveConnectError', 'Failed to connect Google Drive'));
    } finally {
      setGoogleDriveConnecting(false);
    }
  };

  const handleDisconnectGoogleDrive = async () => {
    if (!user) return;

    setError(null);
    setSuccess(false);

    try {
      const { error: deleteError } = await supabase
        .from('google_drive_tokens')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      setGoogleDriveConnected(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('googleDriveDisconnectError', 'Failed to disconnect Google Drive'));
    }
  };

  const handleExportData = async (uploadToDrive: boolean = false) => {
    if (!user) return;

    setExporting(true);
    setError(null);
    setSuccess(false);

    try {
      // Get session token
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      
      if (!accessToken) {
        throw new Error('User session expired. Please log in again.');
      }

      // Call Edge Function directly with fetch to handle blob response
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-all-data`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ upload_to_drive: uploadToDrive }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(errorData.error || `Export failed: ${response.statusText}`);
      }

      // Refresh balance after export
      await refreshBalance();

      // If direct download, handle file download
      if (!uploadToDrive) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        a.download = `pmaster-export-${timestamp}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        // Google Drive upload - response should be JSON
        const data = await response.json();
        if (data?.google_drive_url) {
          setSuccess(true);
          setTimeout(() => setSuccess(false), 5000);
          // Open Google Drive link
          window.open(data.google_drive_url, '_blank');
        }
      }

      // Reload export history
      const { data: historyData } = await supabase
        .from('data_exports')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (historyData) {
        setExportHistory(historyData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('exportError', 'Failed to export data'));
      // Refresh balance in case of error (might have been deducted)
      await refreshBalance();
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="w-full px-0">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('backToHome', "Back to Home")}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-6 h-6 text-gray-700" />
            <h2 className="text-2xl font-bold text-gray-900">{t('settingsTitle', 'Settings')}</h2>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
            <p className="text-green-700">{t('settingsUpdated', 'Settings updated successfully')}</p>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Subscription */}
          <SubscriptionSection />

          {/* Referral Program */}
          {businessProfile?.referral_code && (
            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Gift className="w-5 h-5 text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">{t('referralTitle')}</h3>
              </div>

              <p className="text-sm text-gray-600 mb-4">{t('referralBonusInfo')}</p>

              {/* Referral link display */}
              <div className="mb-4">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 mb-2">
                  <p className="text-xs text-gray-500 mb-1">{t('referralCode')}</p>
                  <p className="text-xl font-bold text-blue-600 tracking-wider">{businessProfile.referral_code}</p>
                  <p className="text-xs text-gray-400 mt-1 break-all">https://pmaster.uz/auth/signup?ref={businessProfile.referral_code}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`https://pmaster.uz/auth/signup?ref=${businessProfile.referral_code}`);
                      setReferralCopied(true);
                      setTimeout(() => setReferralCopied(false), 2000);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                  >
                    {referralCopied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
                    {referralCopied ? t('referralCopied') : t('copyLink')}
                  </button>
                  <button
                    onClick={() => {
                      const referralUrl = `https://pmaster.uz/auth/signup?ref=${businessProfile.referral_code}`;
                      const shareText = `pMaster - ta'mirlash ustaxonasi uchun CRM! Ro'yxatdan o'ting: ${referralUrl}`;
                      if (navigator.share) {
                        navigator.share({ text: shareText, url: referralUrl });
                      } else {
                        navigator.clipboard.writeText(referralUrl);
                        setReferralCopied(true);
                        setTimeout(() => setReferralCopied(false), 2000);
                      }
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                  >
                    <Share2 className="w-4 h-4 text-gray-500" />
                    {t('referralShare')}
                  </button>
                </div>
              </div>

              {/* QR Code */}
              {referralQR && (
                <div className="mb-4 flex justify-center">
                  <div className="p-3 bg-white rounded-lg border border-gray-200 inline-block text-center">
                    <img src={referralQR} alt="Referral QR Code" className="w-32 h-32 mx-auto" />
                    <p className="text-xs text-gray-500 mt-1">QR kod orqali ulashing</p>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-blue-600">{referralStats.total}</p>
                  <p className="text-xs text-blue-700">{t('referralTotalInvited')}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-green-600">{referralStats.completed}</p>
                  <p className="text-xs text-green-700">{t('referralCompleted')}</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-amber-600">{referralStats.totalBonus.toLocaleString()}</p>
                  <p className="text-xs text-amber-700">{t('referralTotalBonus')}</p>
                </div>
              </div>
            </div>
          )}

          {/* SMS Settings */}
          <div className="border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">{t('smsNotificationsTitle', 'SMS Notifications')}</h3>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-700 mb-1">{t('smsNotificationsToggleTitle', 'Send SMS notifications')}</p>
                <p className="text-sm text-gray-500">
                  {t('smsNotificationsToggleDesc', "If disabled, SMS won't be sent in any case")}
                </p>
              </div>
              
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={smsEnabled}
                  onChange={(e) => handleSmsToggle(e.target.checked)}
                  disabled={loading}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {smsEnabled ? (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  {t('smsNotificationsEnabledHint', '✓ SMS notifications are enabled. Customers will receive automatic messages.')}
                </p>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  {t('smsNotificationsDisabledHint', '⚠ SMS notifications are disabled. No SMS will be sent.')}
                </p>
              </div>
            )}
          </div>

          {/* Price Field Settings */}
          <div className="border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">{t('priceFieldTitle', 'Price Field')}</h3>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-700 mb-1">{t('priceFieldToggleTitle', 'Hide price field')}</p>
                <p className="text-sm text-gray-500">
                  {t('priceFieldToggleDesc', 'If enabled, the price field will be hidden on the customer intake screen')}
                </p>
              </div>
              
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={isPriceHidden}
                  onChange={(e) => handlePriceHiddenToggle(e.target.checked)}
                  disabled={loading}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {isPriceHidden ? (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  {t('priceFieldHiddenHint', '✓ Price field is hidden. Users can tap “Show” to reveal it.')}
                </p>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  {t('priceFieldVisibleHint', '✓ Price field is visible. It will be shown automatically on the customer intake screen.')}
                </p>
              </div>
            )}
          </div>

          {/* Telegram Reports */}
          <div className="border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Send className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">{t('telegramReportsTitle', 'Telegram Reports')}</h3>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              {t('telegramReportsDesc', 'Receive daily business reports via Telegram bot. Link your account to get started.')}
            </p>

            {/* Link Status */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {telegramLinked
                      ? t('telegramLinkedStatus', 'Telegram account linked')
                      : t('telegramNotLinkedStatus', 'Telegram account not linked')}
                  </p>
                  {telegramLinked && businessProfile?.telegram_linked_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('telegramLinkedAt', 'Linked')}: {new Date(businessProfile.telegram_linked_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {telegramLinked ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Link Button */}
            {!telegramLinked && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleLinkTelegram}
                  disabled={telegramLinking || loading}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {telegramLinking ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                      {t('telegramLinking', 'Linking...')}
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4" />
                      {t('telegramLinkButton', 'Link Telegram')}
                    </>
                  )}
                </button>
                {linkCode && (
                  <p className="mt-2 text-xs text-gray-500">
                    {t('telegramLinkInstructions', 'Bot opened. Please start the bot in Telegram to complete linking.')}
                  </p>
                )}
              </div>
            )}

            {/* Report Settings (only if linked) */}
            {telegramLinked && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('telegramReportTimeLabel', 'Report time (Tashkent)')}
                  </label>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <input
                      type="time"
                      value={telegramReportTime}
                      onChange={(e) => setTelegramReportTime(e.target.value)}
                      onBlur={(e) => handleTelegramReportTimeSave(e.target.value)}
                      disabled={loading || telegramSaving}
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {t('telegramReportTimeHint', 'Daily reports will be sent at this time')}
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-gray-700 mb-1">{t('telegramReportEnabledLabel', 'Enable daily reports')}</p>
                    <p className="text-sm text-gray-500">
                      {t('telegramReportEnabledDesc', 'Receive daily business statistics via Telegram')}
                    </p>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={telegramReportEnabled}
                      onChange={(e) => handleTelegramReportToggle(e.target.checked)}
                      disabled={loading || telegramSaving}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {telegramReportEnabled ? (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      {t('telegramReportEnabledHint', '✓ Daily reports enabled. Reports will be sent at the selected time.')}
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      {t('telegramReportDisabledHint', 'Daily reports are disabled. Enable to receive reports.')}
                    </p>
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSendTestReport}
                    disabled={telegramSaving || telegramTestSending || loading}
                    className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {telegramTestSending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                        {t('telegramTestSending', 'Sending...')}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {t('telegramTestButton', 'Send test report')}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Data Export */}
          <div className="border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Download className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">{t('dataExportTitle', 'Data Export')}</h3>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              {t('dataExportDesc', 'Export all your business data as a ZIP archive containing XLSX files and JSON backup. Cost: 5,000 UZS per export.')}
            </p>

            {/* Google Drive Connection Status */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {googleDriveConnected
                      ? t('googleDriveConnected', 'Google Drive connected')
                      : t('googleDriveNotConnected', 'Google Drive not connected')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {googleDriveConnected
                      ? t('googleDriveConnectedDesc', 'Exports will be automatically uploaded to your Google Drive')
                      : t('googleDriveNotConnectedDesc', 'Connect Google Drive to automatically upload exports')}
                  </p>
                </div>
                {googleDriveConnected ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Google Drive Connect/Disconnect Button */}
            {!googleDriveConnected ? (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleConnectGoogleDrive}
                  disabled={googleDriveConnecting || loading}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {googleDriveConnecting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                      {t('googleDriveConnecting', 'Connecting...')}
                    </>
                  ) : (
                    <>
                      <Cloud className="w-4 h-4" />
                      {t('googleDriveConnectButton', 'Connect Google Drive')}
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleDisconnectGoogleDrive}
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-4 h-4" />
                  {t('googleDriveDisconnectButton', 'Disconnect Google Drive')}
                </button>
              </div>
            )}

            {/* Export Buttons */}
            <div className="space-y-3">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  {t('exportCost', 'Export cost')}: <strong>5,000 UZS</strong>
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  {balance !== null && balance < 5000
                    ? t('insufficientBalanceExport', { balance: balance.toLocaleString('uz-UZ') })
                    : t('currentBalanceExport', { balance: (balance || 0).toLocaleString('uz-UZ') })}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleExportData(false)}
                  disabled={exporting || loading || (balance !== null && balance < 5000)}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                      {t('exporting', 'Exporting...')}
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      {t('exportDownload', 'Download Export')}
                    </>
                  )}
                </button>

                {googleDriveConnected && (
                  <button
                    type="button"
                    onClick={() => handleExportData(true)}
                    disabled={exporting || loading || (balance !== null && balance < 5000)}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exporting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                        {t('exporting', 'Exporting...')}
                      </>
                    ) : (
                      <>
                        <Cloud className="w-4 h-4" />
                        {t('exportToDrive', 'Export to Google Drive')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Export History */}
            {exportHistory.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  {t('exportHistory', 'Recent Exports')}
                </h4>
                <div className="space-y-2">
                  {exportHistory.map((exportItem) => (
                    <div key={exportItem.id} className="text-xs text-gray-600 flex items-center justify-between">
                      <span>
                        {new Date(exportItem.created_at).toLocaleString('uz-UZ')} - {exportItem.export_type === 'google_drive' ? t('toGoogleDrive', 'To Google Drive') : t('download', 'Download')}
                      </span>
                      {exportItem.google_drive_file_url && (
                        <a
                          href={exportItem.google_drive_file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          {t('view', 'View')}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  return <SettingsContent />;
}

